import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * Mock transport for endpoints with no backend yet.
 *
 * Resolves requests against an in-memory fixture registry instead of the
 * network, while producing the same success/error shapes `fetchBaseQuery`
 * would — so every downstream consumer (cache tags, `toAppError`, loading
 * states) behaves identically to how it will once a real endpoint exists.
 * Swapping this back out for `fetchBaseQuery` in `baseApi.ts` is the only
 * change a real backend requires; no feature code changes.
 */

export interface MockHandlerContext {
  /** Query-string params merged with any `:segment` values matched from the URL path. */
  params: Record<string, unknown> | undefined;
  body: unknown;
}

export type MockHandler = (context: MockHandlerContext) => unknown;

interface RegisteredRoute {
  method: string;
  /** Path split into segments; a segment starting with ":" matches anything. */
  segments: string[];
  handler: MockHandler;
}

const routes: RegisteredRoute[] = [];

/**
 * Registers a handler for a method + path pattern, e.g.
 * `registerMockHandler('PATCH', '/accounts/:id', handler)`. A `:name`
 * segment matches any single path segment and is passed to the handler as
 * `params.name`.
 */
export function registerMockHandler(method: string, path: string, handler: MockHandler): void {
  routes.push({ method: method.toUpperCase(), segments: toSegments(path), handler });
}

function toSegments(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0);
}

function matchRoute(
  method: string,
  urlPath: string,
): { handler: MockHandler; pathParams: Record<string, string> } | undefined {
  const requestSegments = toSegments(urlPath);

  for (const route of routes) {
    if (route.method !== method) continue;
    if (route.segments.length !== requestSegments.length) continue;

    const pathParams: Record<string, string> = {};
    let matched = true;

    for (const [i, routeSegment] of route.segments.entries()) {
      const requestSegment = requestSegments[i];
      if (requestSegment === undefined) {
        matched = false;
        break;
      }

      if (routeSegment.startsWith(':')) {
        pathParams[routeSegment.slice(1)] = decodeURIComponent(requestSegment);
      } else if (routeSegment !== requestSegment) {
        matched = false;
        break;
      }
    }

    if (matched) return { handler: route.handler, pathParams };
  }

  return undefined;
}

/** Simulated network latency, so loading states are visible and testable. */
const MOCK_DELAY_MS = 400;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export const mockBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
) => {
  const rawUrl = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET').toUpperCase();
  // RTK Query types `FetchArgs.params`/`.body` as `any` upstream; narrowed
  // here to what a mock handler is actually allowed to see.
  const queryParams: Record<string, unknown> | undefined =
    typeof args === 'string' ? undefined : (args.params as Record<string, unknown> | undefined);
  const body: unknown = typeof args === 'string' ? undefined : args.body;

  await delay(MOCK_DELAY_MS);

  // Path params only — `fetchBaseQuery` callers pass query params via `args.params`,
  // never appended to `url`, so splitting on "?" is not needed here.
  const urlPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
  const match = matchRoute(method, urlPath);

  if (!match) {
    const error: FetchBaseQueryError = {
      status: 404,
      data: { message: `No mock handler registered for ${method} ${urlPath}.` },
    };
    return { error };
  }

  const params: Record<string, unknown> | undefined =
    queryParams || Object.keys(match.pathParams).length > 0
      ? { ...queryParams, ...match.pathParams }
      : undefined;

  try {
    const data = match.handler({ params, body });
    return { data };
  } catch (thrown) {
    if (isMockHttpError(thrown)) {
      const error: FetchBaseQueryError = {
        status: thrown.status,
        data: thrown.body,
      };
      return { error };
    }

    const error: FetchBaseQueryError = {
      status: 'CUSTOM_ERROR',
      error: thrown instanceof Error ? thrown.message : 'Unknown mock handler error',
    };
    return { error };
  }
};

/** Thrown by a mock handler to simulate a specific HTTP error response. */
export class MockHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Mock HTTP error ${String(status)}`);
    this.name = 'MockHttpError';
  }
}

function isMockHttpError(value: unknown): value is MockHttpError {
  return value instanceof MockHttpError;
}
